import { useMemo, useState } from 'react';
import { Badge, Button, Input, Pagination, Table, type TableColumn } from '@omnitok/ui';
import { Eye, Search } from 'lucide-react';
import type { LocalizationArtifactKind, LocalizationRunItem, LocalizationTargetLocale } from '../types/localization.types';
import LocalizationArtifactViewerDialog from './LocalizationArtifactViewerDialog';
import LocalizationModelSelectionDialog from './LocalizationModelSelectionDialog';
import LocalizationStatusChip from './LocalizationStatusChip';

interface LocalizationItemsTableProps {
  runId: string;
  targetLocales: LocalizationTargetLocale[];
  items: LocalizationRunItem[];
  onDownload: (sku: string, targetLocale: string, kind: LocalizationArtifactKind) => Promise<void>;
  onSelectModel: (sku: string, modelId: string) => Promise<void>;
  isSelectingModel: boolean;
}

export default function LocalizationItemsTable({
  runId,
  targetLocales,
  items,
  onDownload,
  onSelectModel,
  isSelectingModel,
}: LocalizationItemsTableProps) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectionItem, setSelectionItem] = useState<LocalizationRunItem | null>(null);
  const [artifactItem, setArtifactItem] = useState<LocalizationRunItem | null>(null);
  const filtered = useMemo(() => {
    const value = search.trim().toLowerCase();
    return value
      ? items.filter((item) => item.sku.toLowerCase().includes(value) || item.status.includes(value))
      : items;
  }, [items, search]);
  const visible = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

  const columns: TableColumn<LocalizationRunItem>[] = [
    {
      key: 'sku',
      header: 'SKU',
      width: '150px',
      render: (item) => <strong className="localization-mono">{item.sku}</strong>,
    },
    {
      key: 'status',
      header: 'Estado',
      width: '170px',
      render: (item) => <LocalizationStatusChip status={item.status} />,
    },
    {
      key: 'localizations',
      header: 'Localizaciones',
      width: '390px',
      render: (item) => (
        <div className="localization-locale-results">
          {targetLocales.map((targetLocale) => {
            const result = item.localizations?.[targetLocale];
            const status = result?.status ?? item.status;
            const hasSpecificLocalizationStatus = Boolean(
              result?.modelAlreadyLocalized
              || result?.indexadoAlreadyLocalized
              || result?.indexadoNotFound
            );
            return (
              <span key={targetLocale} title={result?.error}>
                <strong>{targetLocale}</strong>
                {!(status === 'already-localized' && hasSpecificLocalizationStatus) && (
                  <LocalizationStatusChip status={status} />
                )}
                {result?.modelAlreadyLocalized && (
                  <Badge size="sm" pill variant="warning">Modelo ya localizado</Badge>
                )}
                {result?.indexadoPartiallyLocalized && (
                  <Badge size="sm" pill variant="success">Indexado localizado parcialmente</Badge>
                )}
                {result?.indexadoAlreadyLocalized && !result.indexadoPartiallyLocalized && (
                  <Badge size="sm" pill variant="warning">Indexado ya localizado</Badge>
                )}
                {result?.indexadoNotFound && (
                  <Badge size="sm" pill variant="neutral">Indexado no encontrado</Badge>
                )}
              </span>
            );
          })}
        </div>
      ),
    },
    {
      key: 'fields',
      header: 'Campos',
      align: 'right',
      width: '90px',
      render: (item) => item.localizableFields ?? '—',
    },
    {
      key: 'changes',
      header: 'Cambios',
      align: 'right',
      width: '90px',
      render: (item) => item.changedFields ?? '—',
    },
    {
      key: 'error',
      header: 'Error',
      render: (item) => (
        <span
          className={`localization-table-error${item.error ? ' localization-table-error--active' : ''}`}
          title={item.error ?? undefined}
        >
          {item.error ?? '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'right',
      width: '190px',
      render: (item) => {
        if (item.status === 'selection-required' && item.candidates?.length) {
          return (
            <Button
              size="xs"
              variant="accent"
              leftIcon={<Eye size={14} />}
              onClick={() => setSelectionItem(item)}
            >
              Comparar modelos
            </Button>
          );
        }
        const hasArtifacts = Boolean(item.artifacts)
          || Object.values(item.localizations ?? {}).some((result) => Boolean(result?.artifacts));
        if (hasArtifacts) {
          return (
            <Button
              size="xs"
              variant="outline"
              leftIcon={<Eye size={14} />}
              onClick={() => setArtifactItem(item)}
            >
              Ver artefactos
            </Button>
          );
        }
        return '—';
      },
    },
  ];

  return (
    <div>
      <div className="localization-table-toolbar">
        <div>
          <h2>Resultados por SKU</h2>
          <p className="localization-mono localization-muted">{runId}</p>
        </div>
        <Input
          className="localization-table-search"
          size="sm"
          aria-label="Buscar SKU o estado"
          placeholder="Buscar SKU o estado"
          leftIcon={<Search size={16} />}
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(0); }}
        />
      </div>

      <div className="localization-table-shell">
        <Table
          columns={columns}
          data={visible}
          rowKey="sku"
          compact
          striped
          emptyMessage="No hay SKU que coincidan con la búsqueda."
        />
      </div>

      <Pagination
        className="localization-pagination"
        currentPage={Math.min(page + 1, totalPages)}
        totalPages={totalPages}
        onChange={(nextPage) => setPage(nextPage - 1)}
        showFirstLast
        showInfo
        totalItems={filtered.length}
        itemsPerPage={rowsPerPage}
        itemsPerPageOptions={[10, 25, 50, 100]}
        onItemsPerPageChange={(nextSize) => { setRowsPerPage(nextSize); setPage(0); }}
        infoPosition="end"
        size="sm"
        language="es"
      />

      <LocalizationModelSelectionDialog
        runId={runId}
        item={selectionItem}
        open={Boolean(selectionItem)}
        isSelecting={isSelectingModel}
        onClose={() => setSelectionItem(null)}
        onSelect={onSelectModel}
      />
      <LocalizationArtifactViewerDialog
        runId={runId}
        item={artifactItem}
        targetLocales={targetLocales}
        open={Boolean(artifactItem)}
        onClose={() => setArtifactItem(null)}
        onDownload={onDownload}
      />
    </div>
  );
}
